import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { postService, hashtagService, Post } from "../services/api";
import { TrendingUp, Eye, Star, MessageCircle, Search, X, ChevronDown, ChevronUp } from "lucide-react";
import toast from "react-hot-toast";
import { SectionHeader } from "../components/SectionHeader";

const Rankings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<Post[]>([]);
  const [hashtags, setHashtags] = useState<{ hashtag: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState<string>("");
  const [selectedHashtag, setSelectedHashtag] = useState<string>(searchParams.get("hashtag") || "");
  const [timePeriod, setTimePeriod] = useState<string>(searchParams.get("time_period") || "");
  const [criteria, setCriteria] = useState<string>(searchParams.get("criteria") || "ranking");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const togglePost = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    if (posts.length > 0 && typeof window !== "undefined" && window.innerWidth >= 1280) {
      const allExpanded: Record<string, boolean> = {};
      posts.forEach((p) => {
        allExpanded[p.id] = true;
      });
      setExpanded(allExpanded);
    }
  }, [posts]);

  useEffect(() => {
    loadData();
    loadHashtags();
  }, [selectedHashtag, timePeriod, criteria]);

  useEffect(() => {
    setSearchInput(selectedHashtag);
  }, [selectedHashtag]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const ranksData = await postService.getRanks(
        selectedHashtag || undefined,
        timePeriod || undefined,
        criteria || undefined
      );
      setPosts(ranksData.posts);
    } catch (error) {
      toast.error("Error loading rankings");
    } finally {
      setLoading(false);
    }
  };

  const loadHashtags = async () => {
    try {
      const hashtagsData = await hashtagService.getPopularHashtags();
      setHashtags(hashtagsData);
    } catch (error) {
      console.error("Error loading hashtags:", error);
    }
  };

  const updateURL = (newHashtag?: string, newTimePeriod?: string, newCriteria?: string) => {
    const params = new URLSearchParams();

    if (newHashtag) params.append("hashtag", newHashtag);
    if (newTimePeriod) params.append("time_period", newTimePeriod);
    if (newCriteria && newCriteria !== "ranking") params.append("criteria", newCriteria);

    setSearchParams(params);
  };

  const handleHashtagSearch = (hashtag: string) => {
    setSelectedHashtag(hashtag);
    setSearchInput(hashtag);
    setShowSuggestions(false);
    updateURL(hashtag, timePeriod, criteria);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    setShowSuggestions(value.length > 0);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanInput = searchInput.replace("#", "").trim();
    setSelectedHashtag(cleanInput);
    setShowSuggestions(false);
    updateURL(cleanInput, timePeriod, criteria);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSelectedHashtag("");
    setShowSuggestions(false);
    updateURL("", timePeriod, criteria);
  };

  const handleTimePeriodChange = (period: string) => {
    setTimePeriod(period);
    updateURL(selectedHashtag, period, criteria);
  };

  const handleCriteriaChange = (newCriteria: string) => {
    setCriteria(newCriteria);
    updateURL(selectedHashtag, timePeriod, newCriteria);
  };

  const filteredHashtagSuggestions = hashtags
    .filter(
      (item) => item.hashtag.toLowerCase().includes(searchInput.toLowerCase()) && item.hashtag !== selectedHashtag
    )
    .slice(0, 5);

  const highlightMatch = (text: string, searchTerm: string) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const truncateContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  const computeScore = (post: Post) => {
    return post.visits * 0.1 + post.ratings_count * 2 + post.comments_count * 1.5 + post.average_rating * 3;
  };

  const getRankPosition = (index: number) => {
    return `#${index + 1}`;
  };

  const getRankClass = (index: number) => {
    if (index === 0) return "bg-yellow-50 border-yellow-200";
    if (index === 1) return "bg-gray-50 border-gray-300";
    if (index === 2) return "bg-orange-50 border-orange-200";
    return "";
  };

  if (loading) {
    return (
      <div className="beat-container">
        <div
          style={{
            // paddingTop: "47px",
            width: "100%",
            background: "rgba(0, 0, 0, 0)",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: "17px",
            display: "flex",
            paddingTop: "17px",
            paddingBottom: "17px",
          }}
        >
          {/* Titulo Pagina */}
          {/* <div
            style={{
              alignSelf: "center",
              height: "44px",
              color: "#262626",
              fontSize: "36px",
              fontFamily: "Abhaya Libre",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            The Pulse
          </div> */}
          <div
            style={{
              alignSelf: "stretch",
              height: "100%",
              color: "#525252",
              fontSize: "18px",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              wordWrap: "break-word",
              textAlign: "center",
            }}
          >
            The Pulse is where Beatpost comes alive. Here you'll find the articles resonating most strongly right now -
            the ones readers can't stop talking about.
          </div>
          {/* Separador horizontal */}
          <div
            className="hidden lg:block"
            style={{
              width: "100%",
              height: "1px",
              backgroundColor: "#D3D3D3",
            }}
          />
        </div>
        <div className="beat-card p-8 mb-8 rounded-lg">
          <div className="beat-loading h-8 mb-4"></div>
          <div className="beat-loading h-4 mb-8"></div>
        </div>
        <div className="articles-list-loading space-y-6 px-11 w-full">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="beat-card p-6">
              <div className="beat-loading h-6 mb-4"></div>
              <div className="beat-loading h-4 mb-2"></div>
              <div className="beat-loading h-32"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="beat-container">
      {/* Header with Search and Filters */}
      <div
        className="beat-header-search-filters"
        style={{
          width: "100%",
          // paddingTop: "47px",
          paddingBottom: "47px",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: "19px",
          display: "inline-flex",
        }}
      >
        <div
          className="beat-header-search-filters-content"
          style={{
            width: "100%",
            background: "rgba(0, 0, 0, 0)",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "17px",
            display: "flex",
            paddingTop: "17px",
            paddingBottom: "17px",
          }}
        >
          {/* <div
            style={{
              alignSelf: "center",
              height: "44px",
              color: "#262626",
              fontSize: "36px",
              fontFamily: "Abhaya Libre",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            The Pulse
          </div> */}
          <div
            style={{
              alignSelf: "stretch",
              height: "100%",
              color: "#525252",
              fontSize: "18px",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              wordWrap: "break-word",
              textAlign: "center",
            }}
          >
            The Pulse is where Beatpost comes alive. Here you'll find the articles resonating most strongly right now -
            the ones readers can't stop talking about.
          </div>
          {/* Separador horizontal */}
          <div
            className="hidden lg:block"
            style={{
              width: "100%",
              height: "1px",
              backgroundColor: "#D3D3D3",
            }}
          />
        </div>

        <div
          style={{
            alignSelf: "stretch",
            padding: "15px",
            background: "#FFFFFF",
            outline: "1px #E5E5E5 solid",
            outlineOffset: "-1px",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "10px",
            display: "flex",
          }}
        >
          <form onSubmit={handleSearchSubmit} style={{ width: "100%" }}>
            <div className="relative" ref={searchRef}>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-newspaper-400 w-5 h-5" />
              <input
                type="text"
                value={searchInput}
                onChange={handleSearchInputChange}
                onFocus={() => setShowSuggestions(searchInput.length > 0)}
                placeholder="Search by hashtag (partial matching supported)..."
                className="w-full pl-10 pr-12 py-3 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-newspaper-400 hover:text-newspaper-600 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {showSuggestions && filteredHashtagSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-newspaper-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredHashtagSuggestions.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => handleHashtagSearch(item.hashtag)}
                    className="px-4 py-3 hover:bg-newspaper-50 cursor-pointer border-b border-newspaper-100 last:border-b-0"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-newspaper-700 font-medium">
                        #{highlightMatch(item.hashtag, searchInput)}
                      </span>
                      <span className="text-newspaper-400 text-sm">{item.count} posts</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </form>
        </div>

        <div className="inline-flex w-full items-center justify-center md:justify-start gap-2 md:gap-[10px] flex-wrap md:flex-nowrap">
          {hashtags.slice(0, 6).map((item, index) => (
            <div
              key={item.hashtag}
              data-property-1={index === 0 ? "Default" : "Variant2"}
              className="flex items-center justify-center gap-[10px] cursor-pointer rounded-[49px] pl-[12px] md:pl-[16px] pr-[12px] md:pr-[16px] pt-[8px] md:pt-[9px] pb-[8px] md:pb-[9px]"
              style={{
                background: selectedHashtag === item.hashtag ? "#262626" : "transparent",
                outline: selectedHashtag === item.hashtag ? "none" : "1px black solid",
                outlineOffset: "-1px",
              }}
              onClick={() => handleHashtagSearch(item.hashtag)}
            >
              <div
                className="text-[13px] md:text-[14px]"
                style={{
                  color: selectedHashtag === item.hashtag ? "white" : "black",
                  fontFamily: "Abhaya Libre ExtraBold",
                  fontWeight: "800",
                  wordWrap: "break-word",
                }}
              >
                #{item.hashtag}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            alignSelf: "stretch",
            padding: "15px",
            background: "#FAFAFA",
            outline: "1px #E5E5E5 solid",
            outlineOffset: "-1px",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "10px",
            display: "inline-flex",
          }}
        >
          <div
            className="w-full"
            style={{
              color: "#525252",
              fontSize: "14px",
              fontFamily: "Abhaya Libre",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            Articles are ranked according to visits, user ratings (1 to 5 stars) and comment activity. Rankings are
            refreshed every 24 hours to keep them fresh.
          </div>
        </div>

        {/* Sort by time period */}
        <div
          className="sort-by-time-period w-full flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-2 sm:gap-4"
          style={{
            height: "auto",
            background: "rgba(0, 0, 0, 0)",
            display: "inline-flex",
          }}
        >
          <div
            className="sm:w-[53px] sm:h-[20px] text-[14px] sm:text-[16px]"
            style={{
              color: "#404040",
              fontFamily: "Abhaya Libre",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            Sort by:
          </div>
          <div className="relative w-full sm:w-auto">
            <select
              value={criteria}
              onChange={(e) => handleCriteriaChange(e.target.value)}
              style={{
                paddingLeft: "15px",
                paddingRight: "40px",
                paddingTop: "8px",
                paddingBottom: "8px",
                background: "white",
                borderRadius: "68px",
                outline: "1px #D4D4D4 solid",
                outlineOffset: "-1px",
                color: "black",
                fontSize: "14px",
                fontFamily: "Abhaya Libre",
                fontWeight: "400",
                border: "none",
                appearance: "none",
                cursor: "pointer",
                minWidth: "120px",
              }}
              className="w-full sm:w-auto"
            >
              <option value="ranking">Higher ranking</option>
              <option value="visits">Most visited</option>
            </select>
            <div
              style={{
                position: "absolute",
                right: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                width: "21px",
                height: "21px",
              }}
            >
              <div
                style={{
                  width: "10.52px",
                  height: "6.32px",
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  background: "black",
                  clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                }}
              />
            </div>
          </div>
          <div className="sm:w-[83.33px] sm:h-[20px] relative" style={{ background: "rgba(0, 0, 0, 0)" }}>
            <div
              className="sm:absolute sm:left-0 sm:top-[1px] text-[14px] sm:text-[16px]"
              style={{
                color: "#404040",
                fontFamily: "Abhaya Libre",
                fontWeight: "400",
                wordWrap: "break-word",
              }}
            >
              Time period:
            </div>
          </div>
          <div className="relative w-full sm:w-auto">
            <select
              value={timePeriod}
              onChange={(e) => handleTimePeriodChange(e.target.value)}
              style={{
                paddingLeft: "15px",
                paddingRight: "40px",
                paddingTop: "8px",
                paddingBottom: "8px",
                background: "white",
                borderRadius: "39px",
                outline: "1px #D4D4D4 solid",
                outlineOffset: "-1px",
                color: "black",
                fontSize: "14px",
                fontFamily: "Abhaya Libre",
                fontWeight: "400",
                border: "none",
                appearance: "none",
                cursor: "pointer",
                minWidth: "100px",
              }}
              className="w-full sm:w-auto"
            >
              <option value="">Since forever</option>
              <option value="24h">Past 24h</option>
              <option value="7d">Past 7 days</option>
              <option value="30d">Past 30 days</option>
            </select>
            <div
              style={{
                position: "absolute",
                right: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                width: "21px",
                height: "21px",
              }}
            >
              <div
                style={{
                  width: "10.52px",
                  height: "6.32px",
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  background: "black",
                  clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Single Column */}
      <div
        className="articles-list space-y-3 sm:space-y-4 md:space-y-6 px-4 sm:px-6 md:px-11"
        style={{
          paddingBottom: "60px",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: "29px",
          display: "flex",
        }}
      >
        {posts.length === 0 ? (
          <div className="beat-card p-8 rounded-lg text-center">
            <TrendingUp className="w-16 h-16 mx-auto mb-4 text-newspaper-400" />
            <h3 className="text-xl font-semibold text-newspaper-700 mb-2">No articles found</h3>
            <p className="text-newspaper-600">
              {selectedHashtag
                ? `No articles found with hashtags containing "${selectedHashtag}"`
                : "No articles have been published yet"}
            </p>
          </div>
        ) : (
          posts.map((post, index) => (
            <div
              className="beat-card-rannkings flex-wrap sm:flex-nowrap "
              key={post.id}
              data-property-1="Variant3"
              style={{
                width: "100%",
                background: "rgba(0, 0, 0, 0)",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: typeof window !== "undefined" && window.innerWidth < 640 && !expanded[post.id] ? "12px" : "28px",
                display: "inline-flex",
                overflow: "hidden",
                marginBottom:
                  typeof window !== "undefined" && window.innerWidth < 640 && !expanded[post.id] ? "8px" : "24px",
              }}
              onClick={() => {
                if (typeof window !== "undefined" && window.innerWidth < 640) {
                  if (!expanded[post.id]) {
                    setExpanded((prev) => ({ ...prev, [post.id]: true }));
                    return;
                  }
                  navigate(`/post/${post.id}`);
                }
              }}
            >
              <div
                className="Filters hidden sm:inline-flex"
                style={{
                  background: "rgba(0, 0, 0, 0)",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  gap: "10px",
                }}
              >
                <div
                  className="hidden sm:block"
                  style={{
                    alignSelf: "stretch",
                    height: "36px",
                    color: "#262626",
                    fontSize: "30px",
                    fontFamily: "Abhaya Libre",
                    fontWeight: "400",
                    wordWrap: "break-word",
                  }}
                >
                  {getRankPosition(index)}
                </div>
              </div>
              {expanded[post.id] && (
                <img
                  style={{
                    width: "143px",
                    height: "143px",
                    background: "#D9D9D9",
                    objectFit: "cover",
                  }}
                  src={post.image || "/Beatpost_defaut_image.png"}
                  alt=""
                />
              )}
              <div
                style={{
                  width: "100%",
                  background: "rgba(0, 0, 0, 0)",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  gap: "9px",
                  display: "inline-flex",
                }}
              >
                <div
                  style={{
                    alignSelf: "stretch",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <Link
                    to={`/post/${post.id}`}
                    style={{ textDecoration: "none", flex: 1 }}
                    onClick={(e) => {
                      if (typeof window !== "undefined" && window.innerWidth < 640) {
                        if (!expanded[post.id]) {
                          e.preventDefault();
                          setExpanded((prev) => ({ ...prev, [post.id]: true }));
                        }
                      }
                    }}
                  >
                    <div
                      style={{
                        color: "#262626",
                        fontSize: "20px",
                        fontFamily: "Abhaya Libre",
                        fontWeight: "700",
                        lineHeight: "25px",
                        wordWrap: "break-word",
                      }}
                    >
                      {post.title}
                    </div>
                  </Link>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePost(post.id);
                    }}
                    className="xl:hidden"
                    style={{
                      background: "transparent",
                      border: "none",
                      paddingLeft: "8px",
                      paddingRight: "8px",
                      cursor: "pointer",
                    }}
                    aria-expanded={!!expanded[post.id]}
                  >
                    {expanded[post.id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                </div>
                {expanded[post.id] && (
                  <div
                    style={{
                      alignSelf: "stretch",
                      color: "#525252",
                      fontSize: "14px",
                      fontFamily: "Instrument Sans",
                      fontWeight: "400",
                      lineHeight: "20px",
                      wordWrap: "break-word",
                    }}
                  >
                    {truncateContent(post.content)}
                  </div>
                )}
                {expanded[post.id] && (
                  <div
                    className="gap-2 flex flex-row items-center"
                    style={{
                      alignSelf: "stretch",
                      background: "rgba(0, 0, 0, 0)",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Link
                      to={`/voice/${post.author_username}`}
                      className="Container flex justify-between items-center flex-row gap-2 hover:text-newspaper-900"
                    >
                      <img
                        src={post.author_avatar || "/default-avatar.png"}
                        alt={post.author_username}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <div
                        style={{
                          width: "130px",
                          height: "16px",
                          fontFamily: "Abhaya Libre",
                          fontWeight: "400",
                          lineHeight: "16px",
                          letterSpacing: "0.6px",
                          wordWrap: "break-word",
                          color: "#737373",
                          fontSize: "14px",
                        }}
                      >
                        By {post.author_username}
                      </div>
                    </Link>

                    {post.hashtags.slice(0, 3).map((tag, tagIndex) => (
                      <div
                        key={tag}
                        onClick={() => handleHashtagSearch(tag)}
                        style={{
                          width: tagIndex === 0 ? "min-content" : "max-content",
                          height: "16px",
                          color: "#A3A3A3",
                          fontSize: "14px",
                          fontFamily: "Abhaya Libre",
                          fontWeight: "400",
                          lineHeight: "16px",
                          wordWrap: "break-word",
                          cursor: "pointer",
                        }}
                      >
                        #{tag}
                      </div>
                    ))}
                  </div>
                )}
                {expanded[post.id] && (
                  <div
                    style={{
                      alignSelf: "stretch",
                      background: "rgba(0, 0, 0, 0)",
                      justifyContent: "space-between",
                      alignItems: "center",
                      display: "inline-flex",
                    }}
                  >
                    <div
                      style={{
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: "8px",
                        display: "flex",
                      }}
                    >
                      <Star className="w-4 h-4" style={{ color: "#737373" }} />

                      <div
                        style={{
                          textAlign: "center",
                          color: "#737373",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre ExtraBold",
                          fontWeight: "800",
                          lineHeight: "16px",
                          letterSpacing: "0.6px",
                          wordWrap: "break-word",
                        }}
                      >
                        {post.average_rating.toFixed(1)}
                      </div>
                    </div>
                    <div
                      style={{
                        alignSelf: "stretch",
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "8px",
                        display: "flex",
                      }}
                    >
                      <Eye className="w-4 h-4" style={{ color: "#737373" }} />

                      <div
                        style={{
                          color: "#737373",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre ExtraBold",
                          fontWeight: "800",
                          wordWrap: "break-word",
                        }}
                      >
                        {post.visits.toLocaleString()} visits
                      </div>
                    </div>
                    <div
                      style={{
                        alignSelf: "stretch",
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "8px",
                        display: "flex",
                      }}
                    >
                      <div
                        style={{
                          color: "#737373",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre ExtraBold",
                          fontWeight: "800",
                          wordWrap: "break-word",
                        }}
                      >
                        score {computeScore(post).toFixed(1)}
                      </div>
                    </div>
                    <div
                      style={{
                        width: "98.12px",
                        alignSelf: "stretch",
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: "8px",
                        display: "flex",
                      }}
                    >
                      <MessageCircle className="w-4 h-4" style={{ color: "#737373" }} />

                      <div
                        style={{
                          color: "#737373",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre ExtraBold",
                          fontWeight: "800",
                          wordWrap: "break-word",
                        }}
                      >
                        {post.comments_count} comments
                      </div>
                    </div>
                    <div
                      className="hidden sm:block"
                      style={{ width: "28.61px", height: "16px", position: "relative", background: "rgba(0, 0, 0, 0)" }}
                    >
                      <div
                        style={{
                          width: "31px",
                          height: "16px",
                          left: "0px",
                          top: "0px",
                          position: "absolute",
                          color: "#525252",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre ExtraBold",
                          fontWeight: "800",
                          wordWrap: "break-word",
                        }}
                      >
                        ↑ +{index + 1}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Rankings;
